import { InterstitialIntro, InterstitialIntroFrame, InterstitialOutro } from '@/api/generated-catch-backend/model';
import type { DTOBlock } from '@/api/generated-catch-backend/model/dTOBlock';
import { PortableText, isPortableTextEmpty } from '@/app/components/questionnaireV2/components/portable-text';
import { useCanSubmit } from '@/app/components/questionnaireV2/hooks/use-can-submit';
import { useLottieData } from '@/app/components/questionnaireV2/utils/lottie-queries';
import type { LottieRefCurrentProps } from 'lottie-react';
import { motion } from 'motion/react';
import React, { useEffect, useRef, useState } from 'react';
import { ThreeDotLoader } from '../../../loader/loader';
import { CalculatingCancerRisk } from './calculating-cancer-risk';
import { CancerMarquee } from './cancer-marquee';
import { RiskMarquee } from './risk-marquee';
import { SlidingDoor } from './sliding-door';

const LazyLottie = React.lazy(() => import('lottie-react'));

export const FRAME_TRANSITION_DURATION = 0.8;

// Helper function to extract plain text from DTOBlock content for typewriter effect
export function extractTextFromDTOBlocks(content: DTOBlock[] | null | undefined): string {
  if (!content || !Array.isArray(content)) return '';

  return content
    .map((block) => (Array.isArray(block?.children) ? block.children.map((span) => span?.text || '').join('') : ''))
    .join(' ');
}

// Helper function to truncate DTOBlock content based on character count
export function truncateDTOBlocks(content: DTOBlock[] | null | undefined, maxChars: number): DTOBlock[] {
  if (!content || !Array.isArray(content) || maxChars <= 0) return [];

  let charCount = 0;
  const truncatedBlocks: DTOBlock[] = [];

  for (const block of content) {
    if (charCount >= maxChars) break;

    const blockCopy = { ...block };

    if (Array.isArray(block?.children)) {
      const truncatedChildren: any[] = [];

      for (const span of block.children) {
        if (charCount >= maxChars) break;

        const spanText = span?.text || '';
        const remainingChars = maxChars - charCount;

        if (spanText.length <= remainingChars) {
          // Include the entire span
          truncatedChildren.push(span);
          charCount += spanText.length;
        } else {
          // Truncate the span text
          const truncatedSpan = {
            ...span,
            text: spanText.slice(0, remainingChars),
          };
          truncatedChildren.push(truncatedSpan);
          charCount = maxChars;
          break;
        }
      }

      blockCopy.children = truncatedChildren;
    }

    truncatedBlocks.push(blockCopy);
  }

  return truncatedBlocks;
}

// Animation type helper function
export const getAnimationType = (animateIn: string) => {
  switch (animateIn) {
    case 'fade-in':
      return { initial: { opacity: 0 }, animate: { opacity: 1 } };
    case 'type-in':
      return { initial: { opacity: 0 }, animate: { opacity: 1 } };
    case 'none':
      return { initial: {}, animate: {} };
    default:
      // Default to fade-in for unknown animation types
      return { initial: { opacity: 0 }, animate: { opacity: 1 } };
  }
};

// Custom hook for frame navigation logic
export const useFrameNavigation = (frames: InterstitialIntroFrame[], onNext?: () => void) => {
  const [currentFrameIndex, setCurrentFrameIndex] = useState(0);

  const handleNext = () => {
    if (currentFrameIndex < frames.length - 1) {
      setCurrentFrameIndex(currentFrameIndex + 1);
    } else {
      onNext?.();
    }
  };

  return {
    currentFrameIndex,
    setCurrentFrameIndex,
    handleNext,
    currentFrame: frames[currentFrameIndex],
  };
};

// Custom hook for auto-advance logic
export const useAutoAdvance = (
  frames: InterstitialIntroFrame[],
  currentFrameIndex: number,
  setCurrentFrameIndex: (index: number) => void,
) => {
  useEffect(() => {
    if (frames.length <= 1) return;

    const currentFrame = frames[currentFrameIndex];

    // Check if current frame should auto-advance
    if (!currentFrame.autoAdvance) return;

    // Use explicit fallback for auto_advance_time (default to 3 seconds)
    const advanceTime =
      currentFrame.autoAdvanceTime && currentFrame.autoAdvanceTime > 0 ? currentFrame.autoAdvanceTime : 3;

    const timeout = setTimeout(() => {
      const nextIndex = currentFrameIndex + 1;

      if (nextIndex >= frames.length) {
        return;
      }

      setCurrentFrameIndex(nextIndex);
    }, advanceTime * 1000);

    return () => {
      clearTimeout(timeout);
    };
  }, [frames, currentFrameIndex, setCurrentFrameIndex]);
};

export const TypewriterText = ({
  content,
  className,
  delay = 0,
  onComplete,
}: {
  content: DTOBlock[] | null | undefined;
  className: string;
  delay?: number;
  onComplete?: () => void;
}) => {
  const [currentCharIndex, setCurrentCharIndex] = useState(0);

  // Extract plain text for typewriter timing
  const text = extractTextFromDTOBlocks(content);

  useEffect(() => {
    setCurrentCharIndex(0);

    const typeText = async () => {
      await new Promise((resolve) => setTimeout(resolve, delay));

      for (let i = 0; i <= text.length; i++) {
        setCurrentCharIndex(i);
        await new Promise((resolve) => setTimeout(resolve, 50)); // 50ms per character
        if (i === text.length) {
          onComplete?.();
        }
      }
    };

    typeText();
  }, [text, delay, onComplete]);

  // Get the truncated content based on current character index
  const truncatedContent = truncateDTOBlocks(content, currentCharIndex);

  return (
    <div className={`${className} relative`}>
      {/* Invisible text to reserve space - prevents layout shift */}
      <div className="invisible" aria-hidden="true">
        <PortableText content={content} />
      </div>

      {/* Visible typewriter text positioned absolutely over the invisible text */}
      <div className="absolute inset-0 flex items-center justify-center">
        <PortableText content={truncatedContent} />
        {currentCharIndex < text.length && (
          <motion.span
            animate={{ opacity: [1, 0] }}
            transition={{ duration: 0.8, repeat: Infinity, repeatType: 'reverse' }}
            style={{ color: 'inherit' }}
          >
            |
          </motion.span>
        )}
      </div>
    </div>
  );
};

export const ActionButton = ({
  text,
  onClick,
  style,
  variant = 'dark',
}: {
  text: string;
  onClick: () => void;
  variant?: 'light' | 'dark';
  style?: React.CSSProperties;
}) => {
  const canSubmit = useCanSubmit();
  const isDisabled = !canSubmit;

  return (
    <button
      onClick={onClick}
      className={`btn-action ${variant === 'light' ? 'btn-action-light' : ''}`}
      style={style}
      disabled={isDisabled}
    >
      {isDisabled ? (
        <ThreeDotLoader />
      ) : (
        <>
          <span>{text}</span>
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </>
      )}
    </button>
  );
};

export const TextContent = ({
  frame,
  textType,
  classNamePrefix,
  classNames = '',
}: {
  frame: InterstitialIntroFrame;
  textType: 'above' | 'below';
  classNamePrefix?: string;
  classNames?: string;
}) => {
  const content = textType === 'above' ? frame.textAbove : frame.textBelow;
  const className = classNamePrefix ? `${classNamePrefix}-text-${textType}` : classNames;

  // Handle empty content
  if (isPortableTextEmpty(content)) return null;

  if (frame.animateIn === 'type-in') {
    const delay = textType === 'above' ? 300 : frame.textAbove ? 1000 : 300;
    return <TypewriterText content={content} className={className} delay={delay} />;
  }

  // Use PortableText for DTOBlock content
  return <PortableText content={content} className={className} />;
};

export const PredefinedGraphic = ({
  interstitial,
  frame,
  classNamePrefix,
  onNext,
}: {
  interstitial: InterstitialIntro | InterstitialOutro;
  frame: InterstitialIntroFrame;
  classNamePrefix: string;
  onNext: () => void;
}) => {
  if (!frame.graphic || frame.graphic.type !== 'predefined') return null;

  switch (frame.graphic.predefinedAnimation) {
    case 'risk-factor-marquee':
      return <RiskMarquee className={`${classNamePrefix}-graphic`} data-content-type="marquee" />;
    case 'cancer-marquee':
      return <CancerMarquee className={`${classNamePrefix}-graphic`} data-content-type="marquee" />;
    case 'calculating-cancer-risk':
      return <CalculatingCancerRisk onNext={onNext} />;
    case 'sliding-door':
      return interstitial.type === 'outro' ? <SlidingDoor onNext={onNext} interstitial={interstitial} /> : null;
    default:
      return null;
  }
};

export const GraphicLottie = React.forwardRef<
  LottieRefCurrentProps,
  {
    srcUrl: string;
    className?: string;
    loop?: boolean;
    autoPlay?: boolean;
    style?: React.CSSProperties;
  }
>(({ srcUrl, className = '', loop = false, autoPlay = true, style }, forwardedRef) => {
  const { data: lottieData, isLoading, error } = useLottieData(srcUrl);
  const lottieRef = React.useRef<LottieRefCurrentProps | null>(null);

  // Forward ref methods to allow parent control of Lottie animation
  React.useImperativeHandle(forwardedRef, () => ({
    play: () => lottieRef.current?.play(),
    stop: () => lottieRef.current?.stop(),
    pause: () => lottieRef.current?.pause(),
    setSpeed: (speed: number) => lottieRef.current?.setSpeed(speed),
    goToAndStop: (value: number, isFrame?: boolean) => lottieRef.current?.goToAndStop(value, isFrame),
    goToAndPlay: (value: number, isFrame?: boolean) => lottieRef.current?.goToAndPlay(value, isFrame),
    setDirection: (direction: any) => lottieRef.current?.setDirection(direction),
    playSegments: (segments: any, forceFlag?: boolean) => lottieRef.current?.playSegments(segments, forceFlag),
    setSubframe: (useSubFrames: boolean) => lottieRef.current?.setSubframe(useSubFrames),
    getDuration: (inFrames?: boolean) => lottieRef.current?.getDuration(inFrames),
    destroy: () => lottieRef.current?.destroy(),
    animationContainerRef: lottieRef.current?.animationContainerRef || React.createRef(),
    animationLoaded: lottieRef.current?.animationLoaded || false,
    animationItem: lottieRef.current?.animationItem,
  }));

  if (isLoading) {
    return <div className="flex h-80 w-full items-center justify-center">Loading animation...</div>;
  }

  if (error || !lottieData) {
    return <div className="flex h-80 w-full items-center justify-center">Failed to load animation</div>;
  }

  return (
    <React.Suspense fallback={<div className="flex h-80 w-full items-center justify-center">Loading animation...</div>}>
      <LazyLottie
        lottieRef={lottieRef}
        animationData={lottieData}
        className={className}
        style={style}
        data-content-type="lottie"
        loop={loop}
        autoplay={autoPlay}
      />
    </React.Suspense>
  );
});

GraphicLottie.displayName = 'GraphicLottie';

export const GraphicContent = ({
  interstitial,
  frame,
  classNamePrefix,
  onNext,
}: {
  interstitial: InterstitialIntro | InterstitialOutro;
  frame: InterstitialIntroFrame;
  classNamePrefix: string;
  onNext: () => void;
}) => {
  const fullHeight = [
    frame.graphic?.predefinedAnimation === 'calculating-cancer-risk',
    frame.graphic?.predefinedAnimation === 'sliding-door',
  ].some((value) => !!value);

  const renderGraphic = () => {
    if (!frame.graphic) return null;

    const graphic = frame.graphic;

    switch (graphic.type) {
      case 'image':
        if (graphic.assetUrl) {
          return (
            <img
              src={graphic.assetUrl}
              alt="Illustration"
              className={`${classNamePrefix}-graphic`}
              data-content-type="image"
              draggable={false}
            />
          );
        }
        break;

      case 'lottie': {
        if (graphic.assetUrl) {
          return <GraphicLottie srcUrl={graphic.assetUrl} className={`${classNamePrefix}-graphic`} />;
        }
        break;
      }

      case 'predefined':
        return (
          <PredefinedGraphic
            interstitial={interstitial}
            frame={frame}
            classNamePrefix={classNamePrefix}
            onNext={onNext}
          />
        );

      default:
        break;
    }

    return null;
  };

  return <div className={`flex w-full ${fullHeight ? 'h-full' : ''} justify-center`}>{renderGraphic()}</div>;
};

const CLOUDFLARE_HLS_URL =
  'https://customer-de0fd926nj9zjpbn.cloudflarestream.com/1df9311bd80853be898772ea31bc1fcb/manifest/video.m3u8';

const SANITY_VIDEO_ASSET =
  'https://cdn.sanity.io/files/jx4oo3l1/production/08b17776632eb44717f0a4a9e014de74caeb4502.mp4';

function shouldUseCloudflareStream(url: string): boolean {
  return url === SANITY_VIDEO_ASSET;
}

const HlsVideo = ({ src, className }: { src: string; className: string }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Safari supports HLS natively
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
      return;
    }

    let cancelled = false;
    let hls: InstanceType<typeof import('hls.js').default> | undefined;

    import('hls.js').then(({ default: Hls }) => {
      if (cancelled || !Hls.isSupported() || !videoRef.current) return;
      hls = new Hls();
      hls.loadSource(src);
      hls.attachMedia(videoRef.current);
    });

    return () => {
      cancelled = true;
      hls?.destroy();
    };
  }, [src]);

  return <video ref={videoRef} className={className} autoPlay muted loop playsInline />;
};

export const BackgroundAsset = ({
  backgroundAsset,
  classNamePrefix = '',
  className = '',
  isActive = true,
}: {
  backgroundAsset: string | null | undefined;
  classNamePrefix?: string;
  className?: string;
  isActive?: boolean;
}) => {
  if (!backgroundAsset || !isActive) return null;

  const isVideo = backgroundAsset.endsWith('.mp4') || backgroundAsset.endsWith('.webm');

  const prefixClass = classNamePrefix ? `${classNamePrefix}-background-asset` : '';
  const baseClasses = 'absolute inset-0 z-[2] h-full w-full object-cover';
  const classes = [baseClasses, prefixClass, className].filter(Boolean).join(' ');

  if (isVideo) {
    if (shouldUseCloudflareStream(backgroundAsset)) {
      return <HlsVideo src={CLOUDFLARE_HLS_URL} className={classes} />;
    }
    return <video className={classes} src={backgroundAsset} autoPlay muted loop playsInline preload="none" />;
  }

  return (
    <div
      className={classes}
      style={{
        backgroundImage: `url(${backgroundAsset})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    />
  );
};

// Helper function to get dynamic styles for graphics
export const getGraphicStyles = ({
  height,
  width,
}: {
  height?: number | null | undefined;
  width?: number | null | undefined;
}) => {
  const styles: React.CSSProperties = {
    maxWidth: '100%',
    maxHeight: '100%',
  };

  // If specific dimensions are provided, use them
  if (width !== null && width !== undefined) {
    styles.width = `${width}px`;
  } else {
    // Default to full width of container
    styles.width = '100%';
  }

  if (height !== null && height !== undefined) {
    styles.height = `${height}px`;
  }

  return styles;
};

// Shared component for rendering text content with DTOBlock support
export const InterstitialTextContent = ({
  content,
  className,
}: {
  content: string | DTOBlock[] | null | undefined;
  className: string;
}) => {
  if (!content) return null;

  if (typeof content === 'string') {
    return <div className={className}>{content}</div>;
  }

  if (Array.isArray(content) && !isPortableTextEmpty(content)) {
    return (
      <div className={className}>
        <PortableText content={content} />
      </div>
    );
  }

  return null;
};

// Shared component for text content blocks with title and subtitle
export const InterstitialTextContentBlock = ({
  title,
  subtitle,
  containerClassName = '',
  textColorClass = 'text-white',
}: {
  title: string | null | undefined;
  subtitle: DTOBlock[] | null | undefined;
  containerClassName?: string;
  textColorClass?: string;
}) => {
  if (!title && !subtitle) return null;
  return (
    <div className={`flex w-full flex-col items-center gap-3 ${containerClassName}`}>
      <InterstitialTextContent
        content={title}
        className={`max-w-[28rem] text-center text-2xl leading-tight font-semibold ${textColorClass} md:text-2xl`}
      />
      <InterstitialTextContent
        content={subtitle}
        className={`max-w-[28rem] text-center text-lg leading-relaxed font-normal ${textColorClass} ${textColorClass === 'text-white' ? 'opacity-90' : ''}`}
      />
    </div>
  );
};
